/**
 * @file LoadingState - Multi-type skeleton/spinner placeholders.
 *
 * Provides four layout-aware loading skeletons so every page and widget can
 * show a structurally accurate placeholder while data is being fetched:
 *
 *   - "list"  : Avatar + two text lines per row (e.g., employee lists)
 *   - "card"  : Image block + title + action buttons (e.g., vehicle cards)
 *   - "table" : Header row + N data rows with 4 columns (e.g., expense tables)
 *   - "page"  : Centered spinner with optional message (default / full-page loads)
 *
 * @example
 *   <LoadingState type="list" count={8} />
 *   <LoadingState type="table" rows={10} />
 *   <LoadingState message="Daten werden geladen..." />
 */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
  Paper
} from '@mui/material';
function LoadingState({ type = 'page', count = 5, rows, message }) {
  // `rows` is a convenience alias so callers can write <LoadingState type="table" rows={10} />
  // instead of the less intuitive `count` for tabular layouts.
  const itemCount = rows || count;

  switch (type) {
    case 'list':
      return (
        <Stack spacing={2}>
          {Array.from({ length: itemCount }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
              </Box>
            </Box>
          ))}
        </Stack>
      );

    case 'card':
      return (
        <Paper sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={140} sx={{ mb: 2, borderRadius: 1 }} />
          <Skeleton variant="text" width="80%" height={28} />
          <Skeleton variant="text" width="60%" height={20} />
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Skeleton variant="rounded" width={80} height={32} />
            <Skeleton variant="rounded" width={80} height={32} />
          </Box>
        </Paper>
      );

    case 'table':
      return (
        <Box>
          {/* Table header skeleton */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} variant="text" width={100} height={24} />
            ))}
          </Box>
          {/* Table rows skeleton */}
          {Array.from({ length: itemCount }).map((_, rowIndex) => (
            <Box key={rowIndex} sx={{ display: 'flex', gap: 2, py: 1.5 }}>
              {Array.from({ length: 4 }).map((_, colIndex) => (
                <Skeleton
                  key={colIndex}
                  variant="text"
                  width={colIndex === 0 ? 150 : 100}
                  height={20}
                />
              ))}
            </Box>
          ))}
        </Box>
      );

    case 'page':
    default:
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            gap: 2,
          }}
        >
          <CircularProgress />
          {message && (
            <Typography variant="body2" color="text.secondary">
              {message}
            </Typography>
          )}
        </Box>
      );
  }
}

LoadingState.propTypes = {
  /** Display type for the loading state */
  type: PropTypes.oneOf(['list', 'card', 'table', 'page']),
  /** Number of skeleton items (for list/table types) */
  count: PropTypes.number,
  /** Alias for count (for table type) */
  rows: PropTypes.number,
  /** Optional loading message (for page type) */
  message: PropTypes.string,
};

LoadingState.defaultProps = {
  type: 'page',
  count: 5,
};

export default LoadingState;
